import {observer} from 'mobx-react';
import {useEffect} from 'react';
import {Link} from 'react-router-dom';
import {Surface} from '@vidazoo/ui-kit';
import {useDashboardsListContext} from '../context/DashboardsListContext';
import UrlsService from '../../../../common/router/services/urlsService.mdl';
import PageMessageLoader from '../../../../common/components/page-message-loader/PageMessageLoader';
import IDashboard from '../../interfaces/IDashboard';
import DashboardStar from './DashboardStar';
import theme from '../view/theme.module.scss';

export const DashboardCard = observer(function DashboardCard({item}: {item: IDashboard}) {
    const {model} = useDashboardsListContext();
    const selected = !!model.cloudManager.dashboardsStarsById[item._id];
    const layouts = item.layouts?.lg || [];
    const url = UrlsService.editDashboard(item._id);
    return <Surface level={1} className={theme.card} data-dashboard-card={item._id}>
        <div className={theme.cardHeader}>
            <button type="button" className={theme.star} aria-label={`${selected ? 'Unfavorite' : 'Favorite'} ${item.name}`}
                aria-pressed={selected} disabled={!model.cloudManager.isCloudReady}
                onClick={() => model.cloudManager.toggleStar(item)}><DashboardStar selected={selected}/></button>
            <Link className={theme.cardTitle} to={url}>{item.name}</Link>
        </div>
        <Link className={theme.cardBody} to={url} aria-label={`Open dashboard ${item.name}`}>
            <div className={theme.count}>Charts: {layouts.length}</div>
            <div className={theme.charts}>
                {layouts.map(layout => {
                    const chart = model.chartsById[layout.i];
                    return chart ? <div className={theme.chart} key={layout.i}>{chart.name} - {chart.type}</div> : null;
                })}
            </div>
        </Link>
    </Surface>;
});

export default observer(function DashboardsList() {
    const {model} = useDashboardsListContext();
    useEffect(() => {
        void model.items.invalidate().fetch();
        void model.cloudManager.initialize();
        void model.chartsManager.navigationItems.fetch();
        model.resetFilter();
    }, [model]);
    const favorites = model.data.filter(item => !!model.cloudManager.dashboardsStarsById[item._id]);
    const grid = (items: IDashboard[], label: string) => <section className={theme.grid} aria-label={label}>
        {items.map(item => <DashboardCard key={item._id} item={item}/>)}
    </section>;

    return <>
        {model.cloudManager.saveFailed && <div role="alert" className={theme.message}>
            Could not save favorites. <button type="button" onClick={model.cloudManager.retrySave}>Retry</button>
        </div>}
        {!model.isLoading && grid(favorites, 'Favorite dashboards')}
        <hr className={theme.separator}/>
        {model.isLoading ? <PageMessageLoader title="Loading Dashboards" description="Please wait..."/> :
            model.items.error ? <div className={theme.message} role="alert">Could not load dashboards.
                <button type="button" onClick={() => {void model.items.fetch();}}>Retry</button></div> :
            grid(model.data, 'All dashboards')}
    </>;
});
